<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { VTextField } from 'vuetify/components';
import { sleep } from 'radash';

import ChevronRightDoubleSvg from '@/assets/svg/chevron-right-double-20.svg?component';
import ChevronLeftDoubleSvg from '@/assets/svg/chevron-left-double-20.svg?component';
import SearchSvg from '@/assets/svg/search-20.svg?component';
import UsersPlusSvg from '@/assets/svg/users-plus.svg?component';
import XCloseIcon from '@/assets/svg/conversations/XCloseIcon.svg?component';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import { isEligibleGlobalSearchQuery } from '@/core/conversations/conversation-index/global-search.util';
import { DrawerService } from '@/core/shared/drawer/drawer.service';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import ProjectService from '@/core/shared/project/project.service';
import { ProjectStatus } from '@/core/shared/project/project.type';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { ResumeUploadSource, TrackingActionName } from '@/core/shared/tracking/tracking-actions';

const drawerService = new DrawerService();
const conversationIndexService = new ConversationIndexService();
const dialogService = new DialogService();
const projectService = new ProjectService();

const COMPACT_BREAKPOINT_PX = 336;

const headerRef = ref<HTMLElement | null>(null);
const isCompact = ref(false);
let resizeObserver: ResizeObserver | null = null;

const currentProject = computed(() => projectService.currentProject);

const isLiveWithCopilot = computed(() => {
  const project = currentProject.value;
  return project?.status === ProjectStatus.LIVE && project?.copilot === true;
});

const isProjectsDrawerOpen = computed(() => drawerService.isProjectsDrawerOpen);

const _collapseProjectsIconLabel = computed(() =>
  drawerService.isProjectsDrawerOpen ? 'Hide jobs' : 'Show jobs',
);
const collapseProjectsIconLabel = ref(_collapseProjectsIconLabel.value);

// ONE-6912: the Candidates-pane search is an account-wide identity lookup
// (name / email / phone). It never mutates the resting job/stage conversation
// list, so clearing or closing search restores the exact pre-search state.
const searchText = computed({
  get: () => conversationIndexService.globalSearchQuery,
  set: (value) => {
    conversationIndexService.globalSearchQuery = value ?? '';
  },
});

const showSearchField = computed({
  get: () => conversationIndexService.showSearchField,
  set: (value) => {
    conversationIndexService.showSearchField = value;
  },
});

const GLOBAL_SEARCH_DEBOUNCE_MS = 500;

// Plain timer instead of radash's debounce: radash's cancel() permanently
// deactivates the debouncer (every later call runs synchronously), and every
// search session hits a cancel path — this cancel only clears the pending run.
let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;

const searchGlobalCandidatesDebounced = () => {
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    searchDebounceTimer = undefined;
    conversationIndexService.searchGlobalCandidates(searchText.value);
  }, GLOBAL_SEARCH_DEBOUNCE_MS);
};

const cancelPendingGlobalSearch = () => {
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = undefined;
};

const clearText = () => {
  cancelPendingGlobalSearch();
  conversationIndexService.clearGlobalCandidateSearch();
  showSearchField.value = false;
};

const onInputFocus = (isFocused: boolean) => {
  if (!isFocused && searchText.value === '') {
    showSearchField.value = false;
  }
};

watch(
  searchText,
  () => {
    if (!isEligibleGlobalSearchQuery(searchText.value)) {
      // Below the minimum query no request is made and no results (or empty
      // state) may linger — the resting pipeline content stays visible.
      cancelPendingGlobalSearch();
      conversationIndexService.resetGlobalSearchResults();
      return;
    }
    searchGlobalCandidatesDebounced();
  },
  {
    immediate: false,
    flush: 'post',
  },
);

function handleUploadClick() {
  if (!isLiveWithCopilot.value || !currentProject.value) return;

  TrackingService.trackAction(TrackingActionName.RESUME_UPLOAD_CTA_CLICKED, {
    source: ResumeUploadSource.PIPELINE_HEADER,
    job_id: currentProject.value.jobId,
  });

  dialogService.uploadResumesModalProps = {
    isOpen: true,
    jobId: currentProject.value.jobId,
    activeOutreachEnabled: currentProject.value.activeOutreachEnabled ?? false,
    source: ResumeUploadSource.PIPELINE_HEADER,
  };
}

// Ensure the collapse icon label changes predictably
watch(isProjectsDrawerOpen, async () => {
  await sleep(300); // Wait for drawer to close
  collapseProjectsIconLabel.value = _collapseProjectsIconLabel.value;
});

onMounted(() => {
  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      isCompact.value = entry.contentRect.width < COMPACT_BREAKPOINT_PX;
    }
  });
});

// Re-attach observer when headerRef appears/disappears (e.g. search toggle)
watch(headerRef, (el, oldEl) => {
  if (oldEl) resizeObserver?.unobserve(oldEl);
  if (el) resizeObserver?.observe(el);
});

onBeforeUnmount(() => {
  cancelPendingGlobalSearch();
  resizeObserver?.disconnect();
});
</script>

<template>
  <div class="h-[34px] w-full">
    <template v-if="showSearchField">
      <div class="relative w-full">
        <v-text-field
          autofocus
          placeholder="Search by name, email, or phone"
          v-model="searchText"
          class="text-pro-field__input"
          density="compact"
          variant="solo"
          clearable
          persistent-clear
          data-test="global-search-input"
          @update:focused="onInputFocus"
          @keydown.esc="clearText"
        >
          <template v-slot:prepend-inner>
            <SearchSvg class="text-shade-900" />
          </template>
          <template v-slot:clear>
            <XCloseIcon class="text-shade-900" @click="clearText" />
          </template>
        </v-text-field>
      </div>
    </template>

    <template v-else>
      <div ref="headerRef" class="flex h-8 items-center justify-between">
        <v-tooltip location="bottom" content-class="rounded-tooltip tooltip-top-arrow">
          <template #activator="{ props }">
            <div
              v-bind="props"
              class="ml-1 cursor-pointer"
              @click="drawerService.toggleProjectsDrawer"
            >
              <ChevronLeftDoubleSvg v-if="drawerService.isProjectsDrawerOpen" />
              <ChevronRightDoubleSvg v-else />
            </div>
          </template>
          <span class="text-xs">{{ collapseProjectsIconLabel }}</span>
        </v-tooltip>

        <div class="ml-2 flex flex-1 items-center justify-between">
          <h2
            class="min-w-0 flex-1 truncate p-0 font-serif text-lg font-black leading-6 text-shade-900"
          >
            Candidates
          </h2>
          <!-- Using <div> instead of <button> because Vuetify's theme engine
               applies unwanted background styles to native <button> elements -->
          <div class="flex items-center gap-1">
            <div
              class="flex items-center rounded-md px-2 py-1 transition-colors"
              :class="
                isLiveWithCopilot
                  ? 'cursor-pointer hover:bg-tint-40'
                  : 'pointer-events-none text-tint-300'
              "
              data-test="upload-button"
              @click="handleUploadClick"
            >
              <span
                v-if="!isCompact"
                class="mr-1 text-xs font-semibold"
                :class="isLiveWithCopilot ? 'text-shade-900' : 'text-tint-300'"
                >Upload</span
              >
              <UsersPlusSvg :class="isLiveWithCopilot ? '' : 'opacity-30'" />
            </div>
            <div
              class="flex cursor-pointer items-center rounded-md px-2 py-1 transition-colors hover:bg-tint-40"
              @click="showSearchField = true"
            >
              <span v-if="!isCompact" class="mr-1 text-xs font-semibold text-shade-900"
                >Search</span
              >
              <SearchSvg />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="postcss">
.text-pro-field__input {
  & .v-field {
    @apply box-border border border-solid border-highlight-500 pr-1 shadow-none !important;
  }
  & .v-field--variant-solo {
    @apply shadow-none !important;
  }
  & input.v-field__input {
    @apply h-8 min-h-0 bg-white p-0 pl-2 text-sm text-shade-900 placeholder:text-tint-300 placeholder:opacity-100 !important;
  }
  & .v-input__details {
    @apply hidden !important;
  }
}
</style>
